import React, { useEffect, useState } from "react";
import uuid from "react-uuid";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { useHistory } from "react-router-dom";
import { FormHelper, StyleHelper } from "utils";
import useMessage from "hooks/useMessage";
import useExceptionHandler from "hooks/useExceptionHandler";
import { InputSidebarContainer } from "components";
import RadioGroup from "components/RadioGroup/RadioGroup";
import ModalContainer from "components/ModalContainer/ModalContainer";
import Attachments from "components/Attachment/Attachment";
import EditHeader from "components/PantallasEdit/Header/EditHeader";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import { RncLookup } from "components";
import useQueryParams from "hooks/useQueryParams";
import { Compania } from "models/Compania";
import { CompaniaManager, TenantManager, SolicitudCompaniaTenantManager as SolicitudManager, CredencialesCertificacionManager } from "managers";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import { useTheme, useAuthentication } from "hooks";
import DropZone from "components/DropZone/Dropzone";
import {
  TIPO_SOLICITUD_ACTIVACION,
  ESTATUS_PENDIENTE
}
  from "models/SolicitudesCompaniaTenant"
import { WarningException } from "utils";

export default function CompaniaVinculadaEdit({ showLoading, hideLoading }) {
  const [compania, setCompania] = useState({});
  const [credenciales, setCredenciales] = useState({});
  const [tituloHeader, setTituloHeader] = useState("Solicitud de Creación de Compañía");
  const [mostrarConfiguracionSistemaExterno, setMostrarConfiguracionSistemaExterno] = useState(false);
  const [showAdjuntarDocumentosModal, setShowAdjuntarDocumentosModal] = useState();
  const [modal, setModal] = useState(false);
  const [isReadonly, setIsReadonly] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [rncLookupKey, setRncLookupKey] = useState(uuid());

  const { usuario, tienePermiso } = useAuthentication();

  const URLParameters = useQueryParams();
  const requestId = URLParameters.get("requestId");
  const companiaId = URLParameters.get("companiaId");
  const esNuevoRequest = !requestId && !companiaId;
  const esCompaniaDirecta = !!companiaId;
  const { Message, cerrarAlertas, Types, Warning } = useMessage();
  const { handleException } = useExceptionHandler();
  const { theme, vendorTheme } = useTheme();
  const history = useHistory();

  const CERTIFICADA_SI = true;
  const CERTIFICADA_NO = false;
  const CERTIFICADA_SI_LABEL = "Sí";
  const CERTIFICADA_NO_LABEL = "No";
  const CERTIFICADA_LIST = [
    { value: CERTIFICADA_SI, label: CERTIFICADA_SI_LABEL },
    { value: CERTIFICADA_NO, label: CERTIFICADA_NO_LABEL }
  ];

  useEffect(() => {
    if (!usuario?.Compania?.EsTenant) {
      Warning({
        message: `Usuario no tiene permiso.`
      });
      return;
    }

    if (requestId) {
      showLoading();
      cargarCompania(requestId, true);
      setTituloHeader("Editar Solicitud");
    } else if (companiaId) {
      showLoading();
      cargarCompania(companiaId, false);
      setTituloHeader("Editar Compañía");
    } else {
      setDefaultValues();
      cerrarAlertas();
    }
    hideLoading();
    return () => { };
  }, [requestId, companiaId]);

  const onFileChange = (propFile, propName) => {
    const nuevasCredenciales = { ...credenciales, [propName]: propFile };
    setCredenciales(nuevasCredenciales);

    const certificadoEnviado = Array.isArray(propFile)
      ? propFile.length > 0
      : !!propFile;

    setCompania((prev) => ({
      ...prev,
      CertificadoEnviado: certificadoEnviado,
    }));
  };

  const onLogoChange = (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) {
      setCompania((prev) => ({ ...prev, Logo: null }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) =>
      setCompania((prev) => ({ ...prev, Logo: e.target.result }));
    reader.readAsDataURL(file);
  };

  const setDefaultValues = () => {
    let color1 = "#F16101";
    let color2 = "#8AA8B0";
    let color3 = "#FFFFFF";

    if (theme.title == "GN ECF") {
      color1 = "#D52B1E";
      color2 = "#1097AF";
      color3 = "#FFFFFF";
    }

    setCompania({
      ...Compania,
      Estatus: Compania.DEFAULT_ESTATUS_VALUE,
      Ambiente: Compania.DEFAULT_AMBIENTE_VALUE,
      Nombre: "",
      RazonSocial: "",
      Codigo: "",
      RNC: "",
      DireccionLinea1: "",
      TelefonoPrincipal: "",
      TelefonoSecundario: "",
      Email: "",
      Color1: color1,
      Color2: color2,
      Color3: color3,
      PaginaWeb: "",
      Extension1: "",
      Extension2: "",
      Fax: "",
      ConfiguracionSistemaExterno: "",
      ClienteId: 0,
      EsPrincipal: false,
      Certificada: false,
      EstadoCertificacion: Compania.ESTADO_CERTIFICACION_PENDIENTE,
      Logo: "",
    });
    setMostrarConfiguracionSistemaExterno(false);
  };

  const cargarCompania = (Id, esRequest) => {
    if (esRequest) {
      SolicitudManager
        .buscarSolicitudCompaniaTenantPorId({ IdSec: requestId, CompaniaId: usuario.CompaniaId })
        .then((data) => {
          data.compania.Color1 = "#" + data.compania.Color1;
          data.compania.Color2 = "#" + data.compania.Color2;
          data.compania.Color3 = "#" + data.compania.Color3;
          setCompania({ ...data.compania });

          const isReadonly = data.solicitud.Estatus != ESTATUS_PENDIENTE
          setIsReadonly(isReadonly);
        });
    } else {
      CompaniaManager
        .buscarPorId(Id)
        .then((data) => {
          data.Color1 = "#" + data.Color1;
          data.Color2 = "#" + data.Color2;
          data.Color3 = "#" + data.Color3;

          setCompania({ ...data, Id });
        });
    }
  };

  const actualizarFlagsCredenciales = (payload) => {
    const rncEnviado = `${payload.RNCCedula}`.trim().length > 0;
    const passwordEnviado = `${payload.Contrasena}`.trim().length > 0;
    const keyCertificadoEnviado = `${payload.KeyCertificado}`.trim().length > 0;
    const certificadoEnviado = Array.isArray(payload.Certificado)
      ? payload.Certificado.length > 0
      : !!payload.Certificado;

    setCompania((prev) => ({
      ...prev,
      RncEnviado: rncEnviado,
      PasswordEnviado: passwordEnviado,
      KeyCertificadoEnviado: keyCertificadoEnviado,
      CertificadoEnviado: certificadoEnviado,
    }));

    return rncEnviado && passwordEnviado && keyCertificadoEnviado && certificadoEnviado;
  };

  const enviarCredenciales = (payload) => {
    const data = payload || {
      RNCCedula: credenciales.RnccedulaCertificacion || "",
      Contrasena: credenciales.PasswordCertificacion || "",
      KeyCertificado: credenciales.KeyCertificado || "",
      Certificado: credenciales.Certificado || [],
    };

    const credencialesCompletas = actualizarFlagsCredenciales(data);
    if (!credencialesCompletas) {
      return;
    }

    showLoading?.();
    CredencialesCertificacionManager.enviar(data)
      .then(() => {
        Message({
          message: "Credenciales enviadas correctamente.",
          autoDismiss: 1.5,
        });
        setCredenciales({});
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading?.());
  };

  function grabar() {
    showLoading();
    if (esNuevoRequest) {
      const companiaNueva = {
        ...compania,
        CompaniaTenantId: usuario.CompaniaId
      };

      setCompania(companiaNueva);
      const formularioJson = JSON.stringify(companiaNueva);

      const solicitudActivacion = {
        TipoSolicitud: TIPO_SOLICITUD_ACTIVACION,
        FormularioJson: formularioJson
      };

      SolicitudManager
        .crearCompaniaVinculada(solicitudActivacion)
        .then((_res) => {
          Message({ message: `Solicitud creada exitosamente` });

          const payloadCredenciales = {
            RNCCedula: credenciales.RnccedulaCertificacion || "",
            Contrasena: credenciales.PasswordCertificacion || "",
            KeyCertificado: credenciales.KeyCertificado || "",
            Certificado: credenciales.Certificado || [],
            NombreCompaniaSolicitada: compania.Nombre || "",
            CodigoCompaniaSolicitada: compania.Codigo || "",
            RncCompaniaSolicitada: compania.RNC || "",
            RazonSocial: compania.RazonSocial || "",
          };

          if (actualizarFlagsCredenciales(payloadCredenciales)) {
            enviarCredenciales(payloadCredenciales);
          }

          let urlBase = `/CompaniaVinculadaEdit?requestId=${_res.Data.IdSec}`;
          history.push(urlBase);
        })
        .catch((e) => {
          handleException(e)
          return;
        })
        .finally(() => {
          hideLoading();
        });
    } else if (esCompaniaDirecta) {
      CompaniaManager
        .editarVinculada(compania)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          cargarCompania(companiaId, false);
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
        });
    } else {
      const formularioJsonEdit = JSON.stringify(compania);

      const solicitudEditRequest = {
        IdSec: requestId,
        CompaniaId: usuario.CompaniaId,
        TipoSolicitud: TIPO_SOLICITUD_ACTIVACION,
        FormularioJson: formularioJsonEdit
      };

      SolicitudManager
        .editarSolicitudCompaniaTenant(solicitudEditRequest)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          cargarCompania(requestId, true);
          const payloadCredenciales = {
            RNCCedula: credenciales.RnccedulaCertificacion || "",
            Contrasena: credenciales.PasswordCertificacion || "",
            KeyCertificado: credenciales.KeyCertificado || "",
            Certificado: credenciales.Certificado || [],
            NombreCompaniaSolicitada: compania.Nombre || "",
            CodigoCompaniaSolicitada: compania.Codigo || "",
            RncCompaniaSolicitada: compania.RNC || "",
            RazonSocial: compania.RazonSocial || "",
          };

          const credencialesCompletas = actualizarFlagsCredenciales(payloadCredenciales);
          if (credencialesCompletas) {
            enviarCredenciales(payloadCredenciales);
          }
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
        });
    }
  }

  const handleVolverClick = () => {
    let urlBase = `/SolicitudCompaniaTenantList?t=true`;
    if (esCompaniaDirecta) {
      urlBase = `/CompaniaVinculadaList`;
    }

    history.push(urlBase);
  }

  const adjuntarDocumentoModal = (
    <ModalContainer
      showModal={showAdjuntarDocumentosModal}
      size="lg"
      title="Adjuntar Documentos"
      btnGrabar={{
        show: false
      }}
      cerrar={() => {
        setShowAdjuntarDocumentosModal(false);
      }}
    >
      <EditSection>
        <Attachments
          readonly={isReadonly}
          showLoading={() => { }}
          hideLoading={() => { }}
          companiaId={usuario.CompaniaId}
          fuente="SolicitudesCompaniaTenant"
          modulo="ECF"
          referencia={requestId}
          ocultarHeader={true}
          modal={modal}
          setModal={setModal}
          setAttachmentCount={setAttachmentCount}
        />
      </EditSection>
    </ModalContainer>
  )

  return (
    <>
      {showAdjuntarDocumentosModal && adjuntarDocumentoModal}

      <Form action="" className="form-horizontal" method="">
        <Card>
          <EditHeader
            titulo={`${tituloHeader}`}
            volver={true}
            volverFunc={handleVolverClick}
            {...!isReadonly && { grabar: () => grabar() }}
            {...!esNuevoRequest && !esCompaniaDirecta && { adjuntarDocumento: () => setShowAdjuntarDocumentosModal(true) }}
          />
          <EditContainer>
            <EditSection title="Información General" style={{}}>
              {!esNuevoRequest && (
                <SectionRow>
                  <InputContainer stacked label="Id">
                    <Form.Control
                      type="number"
                      name="Id"
                      value={compania.Id}
                      onChange={(e) => FormHelper.handleChange(e, setCompania)}
                      disabled={true}
                    />
                  </InputContainer>
                </SectionRow>
              )}

              <SectionRow>
                <InputContainer stacked label="Documento">
                  <RncLookup
                    key={rncLookupKey}
                    required={true}
                    initialValue={{
                      value: compania.RNC ?? "",
                      label: compania.RNC ?? "",
                      Rnc: compania.RNC ?? "",
                      RazonSocial: compania.RazonSocial ?? "",
                    }}
                    onChange={(e) => {
                      setCompania({
                        ...compania,
                        RNC: e.Rnc,
                        RazonSocial: e.RazonSocial,
                      });
                    }}
                    disabled={isReadonly}
                  />
                </InputContainer>

                <InputContainer stacked label="Razón Social">
                  <Form.Control
                    type="text"
                    name="RazonSocial"
                    value={compania.RazonSocial}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer stacked label="Nombre Comercial">
                  <Form.Control
                    type="text"
                    name="Nombre"
                    value={compania.Nombre}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>

                <InputContainer stacked label="Código">
                  <Form.Control
                    type="text"
                    name="Codigo"
                    value={compania.Codigo}
                    onChange={(e) =>
                      FormHelper.handleChangeNoEspace(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow multilinea>
                <InputContainer stacked label="Dirección">
                  <Form.Control
                    type="text"
                    name="DireccionLinea1"
                    value={compania.DireccionLinea1}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            <EditSection title="Información de Contacto" style={{}}>
              <SectionRow columns={3}>
                <InputContainer stacked label="Teléfono Principal">
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <Form.Control
                      type="text"
                      name="TelefonoPrincipal"
                      placeholder="Teléfono Principal"
                      value={compania.TelefonoPrincipal}
                      onChange={(e) =>
                        FormHelper.handleChangeSoloNumero(e, setCompania)
                      }
                      disabled={isReadonly}
                      style={{ flex: 3 }}
                    />
                    <Form.Control
                      type="text"
                      name="Extension1"
                      placeholder="Ext."
                      value={compania.Extension1}
                      onChange={(e) =>
                        FormHelper.handleChangeSoloNumero(e, setCompania)
                      }
                      disabled={isReadonly}
                      style={{ flex: 1 }}
                    />
                  </div>
                </InputContainer>

                <InputContainer stacked label="Teléfono Secundario">
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <Form.Control
                      type="text"
                      name="TelefonoSecundario"
                      placeholder="Teléfono Secundario"
                      value={compania.TelefonoSecundario}
                      onChange={(e) =>
                        FormHelper.handleChangeSoloNumero(e, setCompania)
                      }
                      disabled={isReadonly}
                      style={{ flex: 3 }}
                    />
                    <Form.Control
                      type="text"
                      name="Extension2"
                      placeholder="Ext."
                      value={compania.Extension2}
                      onChange={(e) =>
                        FormHelper.handleChangeSoloNumero(e, setCompania)
                      }
                      disabled={isReadonly}
                      style={{ flex: 1 }}
                    />
                  </div>
                </InputContainer>

                <InputContainer stacked label="Correo">
                  <Form.Control
                    type="text"
                    name="Email"
                    value={compania.Email}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow columns={3}>
                <InputContainer stacked label="Fax">
                  <Form.Control
                    type="text"
                    name="Fax"
                    value={compania.Fax}
                    onChange={(e) =>
                      FormHelper.handleChangeSoloNumero(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>

                <InputContainer stacked label="Página Web">
                  <Form.Control
                    type="text"
                    name="PaginaWeb"
                    value={compania.PaginaWeb}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            <EditSection title="Información de Certificación de Facturación Electrónica" style={{}}>
              <SectionRow>
                <InputContainer stacked label="¿Esta Compañía está Certificada?" labelSize={6}>
                  <RadioGroup
                    options={CERTIFICADA_LIST}
                    selectedOption={compania.Certificada}
                    disabled={isReadonly}
                    onChange={(e) => {
                      console.log(e);
                      setCompania({
                        ...compania,
                        Certificada: JSON.parse(e.target.value)
                      });
                    }}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            {compania.Certificada === false && (
              <>
                <SectionRow multilinea>
                  <InputContainer stacked label="Estado de Certificación">
                    <RadioGroup
                      options={Compania.ESTADO_CERTIFICACION_LIST}
                      selectedOption={compania.EstadoCertificacion}
                      onChange={(e) => {
                        setCompania({
                          ...compania,
                          EstadoCertificacion: e.target.value
                        });
                      }}
                      disabled={isReadonly}
                    />
                  </InputContainer>
                </SectionRow>

                <EditSection>
                  <SectionRow columns={3}>
                    <EditSection title="Credenciales del Portal de Certificación de la DGII" style={{}}>
                      <InputContainer stacked label="RNC / Cédula">
                        <Form.Control
                          type="text"
                          name="RnccedulaCertificacion"
                          value={credenciales.RnccedulaCertificacion || ""}
                          onChange={(e) => {
                            FormHelper.handleChangeSoloNumero(e, setCredenciales)
                            setCompania((prev) => ({
                              ...prev,
                              RncEnviado: `${e.target.value || ""}`.trim().length > 0,
                            }))
                          }}
                          disabled={isReadonly}
                        />
                      </InputContainer>
                    </EditSection>

                    <EditSection title="Credenciales de la Firma Digital (Certificado Digital)" style={{}}>
                      <InputContainer stacked label="Key Certificado">
                        <Form.Control
                          type="password"
                          autoComplete="off"
                          name="KeyCertificado"
                          value={credenciales.KeyCertificado || ""}
                          onChange={(e) => {
                            FormHelper.handleChange(e, setCredenciales)
                            setCompania((prev) => ({
                              ...prev,
                              KeyCertificadoEnviado: `${e.target.value || ""}`.trim().length > 0,
                            }))
                          }}
                          disabled={isReadonly}
                        />
                      </InputContainer>

                    </EditSection>
                  </SectionRow>

                  <SectionRow columns={3}>
                    <InputContainer stacked label="Contraseña">
                      <Form.Control
                        type="password"
                        autoComplete="off"
                        name="PasswordCertificacion"
                        value={credenciales.PasswordCertificacion || ""}
                        onChange={(e) => {
                          FormHelper.handleChange(e, setCredenciales)
                          setCompania((prev) => ({
                            ...prev,
                            PasswordEnviado: `${e.target.value || ""}`.trim().length > 0,
                          }))
                        }}
                        disabled={isReadonly}
                      />
                    </InputContainer>

                    <InputContainer stacked label="Certificado">
                      <DropZone
                        label="Seleccione un archivo .p12"
                        updateFilesCb={(f) => onFileChange(f, "Certificado")}
                        multiple={false}
                        type="p12"
                      />
                    </InputContainer>


                  </SectionRow>
                </EditSection>
              </>
            )}

            <EditSection title="Otras Informaciones" style={{}}>
              <SectionRow columns={6}>
                <InputContainer stacked label="Cantidad de PT">
                  <Form.Control
                    type="number"
                    name="CantidadPT"
                    value={compania.CantidadPT}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            <EditSection title="Configuración de la Apariencia" style={{}}>
              <SectionRow columns={3}>
                <InputContainer stacked label="Logo">
                  {compania.Logo && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: 8 }}>
                      <img
                        src={compania.Logo}
                        alt="Logo"
                        style={{ maxHeight: 80 }}
                      />
                      {!isReadonly && (
                        <i
                          className="fa fa-trash"
                          role="button"
                          title="Eliminar logo"
                          onClick={() => onLogoChange(null)}
                          style={{ cursor: "pointer" }}
                        />
                      )}
                    </div>
                  )}
                  <DropZone
                    label="Seleccionar Imagen"
                    updateFilesCb={(f) => onLogoChange(f)}
                    multiple={false}
                    type="image"
                    showFiles={false}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            <EditSection title="Colores del Menú" style={{}}>
              <SectionRow triple={true}>
                <InputContainer stacked label="Primario" labelSize={3}>
                  <Form.Control
                    name="Color1"
                    type="color"
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    value={compania.Color1}
                    disabled={isReadonly}
                  />
                </InputContainer>
                <InputContainer stacked
                  label="Secundario"
                  labelSize={3}
                  labelClassName="pl-2 d-flex"
                >
                  <Form.Control
                    name="Color2"
                    type="color"
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    value={compania.Color2}
                    disabled={isReadonly}
                  />
                </InputContainer>
                <InputContainer stacked label="Logo">
                  <Form.Control
                    name="Color3"
                    type="color"
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    value={compania.Color3}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>
          </EditContainer>
        </Card>
      </Form >
    </>
  )
}